import { useGetGroupDashboard } from '@shared/api/hooks/group';
import { Icons } from '@shared/components/content/icons';
import { RATING_ORDER } from '@shared/components/chart/configs';
import SimpleAreaChart from '@shared/components/chart/simple-area-chart';
import SimpleBarChart from '@shared/components/chart/simple-bar-chart';
import Text from '@shared/components/content/text';
import { ChartCard } from '@shared/components/dashboard/chart-card';
import { DashboardGrid } from '@shared/components/dashboard/dashboard-grid';
import { DashboardSection } from '@shared/components/dashboard/dashboard-section';
import { MetricCard } from '@shared/components/dashboard/metric-card';
import Loader from '@shared/components/loader';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { ChartConfig } from '@shared/components/ui/chart';
import { EmissionsCategoryEnum, GroupMainDashboard } from '@shared/api/types';
import { formatCompact, formatTonnes } from '@shared/lib/format';
import { ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import GroupPage from '../components/group-page';
import { GROUP_ROUTES, isSectionUnlocked } from '../config/pages';
import { useGroupContext } from '../group-context';
import { useGroupFilterSearch } from '../hooks/use-group-filters';

const ORGS_CHART_CONFIG = {
  organizationsCount: {
    label: 'Organisations',
    color: 'var(--chart-3)',
  },
} satisfies ChartConfig;

const EMISSIONS_CHART_CONFIG = {
  totalEmissionsTonnes: {
    label: 'Emissions',
    color: 'var(--chart-3)',
  },
} satisfies ChartConfig;

// Single series — each bar takes its colour from the row's `fill` value.
const RATING_DISTRIBUTION_CHART_CONFIG = {
  items: {
    label: 'Items',
  },
} satisfies ChartConfig;

/** Latest entry of a year-keyed time series, or null when empty. */
function latestEntry<T extends { year: number }>(series: readonly T[]) {
  if (!series.length) return null;
  return [...series].sort((a, b) => a.year - b.year)[series.length - 1];
}

/** Emphasised figure within a metric card's subtle description line. */
function DescriptionValue({ children }: { children: ReactNode }) {
  return <span className="font-semibold text-gray-700">{children}</span>;
}

/**
 * Distribution-chart axis tick: the rating's cloud icon with its share of
 * products beneath. Rendered by recharts inside the SVG, so the HTML bits
 * are wrapped in a foreignObject. `x`/`y`/`payload` are injected by recharts.
 */
function RatingAxisTick({
  x,
  y,
  payload,
  percentByRating,
}: {
  x?: number;
  y?: number;
  payload?: { value: EmissionsCategoryEnum };
  percentByRating: Partial<Record<EmissionsCategoryEnum, number>>;
}) {
  if (x == null || y == null || !payload) return null;
  return (
    <foreignObject x={x - 24} y={y + 4} width={48} height={60}>
      <div className="flex flex-col items-center gap-1">
        <RatingCloud rating={payload.value} className="w-8" />
        <Text variant="subtle" className="text-xs">
          {percentByRating[payload.value] ?? 0}%
        </Text>
      </div>
    </foreignObject>
  );
}

export default function GroupDashboardPage() {
  const { groupFeatures } = useGroupContext();
  const { data, status, isError } = useGetGroupDashboard();

  const companyUnlocked = isSectionUnlocked('company', groupFeatures);
  const purchasingUnlocked = isSectionUnlocked('purchasing', groupFeatures);
  const productsUnlocked = isSectionUnlocked('products', groupFeatures);

  if (isError) throw new Error('Failed to load group dashboard');

  return (
    <GroupPage pageKey="dashboard" lastRefreshedAt={data?.lastRefreshedAt}>
      {status === 'loading' && <Loader className="h-96" />}
      {!!data && (
        <div className="flex flex-col gap-8 pb-12">
          <HeroMetricBar
            data={data}
            companyUnlocked={companyUnlocked}
            purchasingUnlocked={purchasingUnlocked}
            productsUnlocked={productsUnlocked}
          />
          <CompanyReportsSection data={data} unlocked={companyUnlocked} />
          <PurchasedGoodsSection data={data} unlocked={purchasingUnlocked} />
          <ProductsSection data={data} unlocked={productsUnlocked} />
        </div>
      )}
    </GroupPage>
  );
}

function HeroMetricBar({
  data,
  companyUnlocked,
  purchasingUnlocked,
  productsUnlocked,
}: {
  data: GroupMainDashboard;
  companyUnlocked: boolean;
  purchasingUnlocked: boolean;
  productsUnlocked: boolean;
}) {
  const totalCompanyReports = data.totalCompanyReports;
  const totalPurchases = data.totalPurchaseVolume;
  // Carry the global filters (URL params) into the drillthrough targets.
  const filterSearch = useGroupFilterSearch();

  return (
    <DashboardGrid className="md:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        title="Organisations"
        value={data.totalOrganizations}
        description={
          <>
            with{' '}
            <DescriptionValue>
              {data.totalUsers.toLocaleString('en-GB')}
            </DescriptionValue>{' '}
            users
          </>
        }
        to={GROUP_ROUTES.ORGANIZATIONS + filterSearch}
      />
      <MetricCard
        title="Company emissions"
        value={
          data.totalCompanyEmissionsTonnes != null
            ? formatCompact(data.totalCompanyEmissionsTonnes)
            : '-'
        }
        unit={data.totalCompanyEmissionsTonnes != null ? 'tCO₂e' : undefined}
        description={
          totalCompanyReports ? (
            <>
              from <DescriptionValue>{totalCompanyReports}</DescriptionValue>{' '}
              company reports
            </>
          ) : undefined
        }
        to={GROUP_ROUTES.COMPANY_EMISSIONS + filterSearch}
        locked={!companyUnlocked}
      />
      <MetricCard
        title="Purchasing emissions"
        value={
          data.totalPurchasingEmissionsTonnes != null
            ? formatCompact(data.totalPurchasingEmissionsTonnes)
            : '-'
        }
        unit={data.totalPurchasingEmissionsTonnes != null ? 'tCO₂e' : undefined}
        description={
          totalPurchases ? (
            <>
              from{' '}
              <DescriptionValue>
                {formatCompact(totalPurchases)}
              </DescriptionValue>{' '}
              purchases
            </>
          ) : undefined
        }
        to={GROUP_ROUTES.PURCHASING_EMISSIONS + filterSearch}
        locked={!purchasingUnlocked}
      />
      <MetricCard
        title="Product assessments"
        value={
          data.averageCarbonIntensity != null ? (
            <span className="flex items-center gap-2.5">
              <RatingCloud
                rating={data.averageEmissionsCategory}
                className="w-10"
              />
              <span>{data.averageCarbonIntensity.toFixed(2)}</span>
            </span>
          ) : (
            '-'
          )
        }
        unit={data.averageCarbonIntensity != null ? 'kgCO₂e/kg' : undefined}
        description={
          <>
            average of{' '}
            <DescriptionValue>
              {formatCompact(data.totalProductCount)}
            </DescriptionValue>{' '}
            products
          </>
        }
        to={GROUP_ROUTES.PRODUCTS + filterSearch}
        locked={!productsUnlocked}
      />
    </DashboardGrid>
  );
}

function LockedSection({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <section className="flex flex-col gap-1 border-b border-gray-200 pb-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Text variant="cardTitle" className="text-gray-400">
            {title}
          </Text>
          <Text variant="subtle" className="text-gray-400">
            {description}
          </Text>
        </div>
        <Badge size="sm">Not enabled</Badge>
      </div>
    </section>
  );
}

function ExploreButton({ label, to }: { label: string; to: string }) {
  const navigate = useNavigate();
  // Carry the global filters (URL params) into the drillthrough target.
  const filterSearch = useGroupFilterSearch();
  return (
    <Button
      variant="ghost"
      className="text-teal-600 hover:bg-teal-50 hover:text-teal-700"
      onClick={() => navigate(to + filterSearch)}
    >
      {label}
      <Icons.arrowRight className="ml-1.5 size-4" />
    </Button>
  );
}

function CompanyReportsSection({
  data,
  unlocked,
}: {
  data: GroupMainDashboard;
  unlocked: boolean;
}) {
  const description = 'Full company carbon emissions, across Scopes 1, 2 and 3';

  if (!unlocked) {
    return <LockedSection title="Company reports" description={description} />;
  }

  return (
    <DashboardSection
      title="Company reports"
      description={description}
      actions={
        <ExploreButton
          label="Explore company emissions"
          to={GROUP_ROUTES.COMPANY_EMISSIONS}
        />
      }
    >
      <DashboardGrid className="lg:grid-cols-2">
        <ChartCard
          title="Organisations reporting company emissions"
          description={`Of ${data.totalOrganizations} member organisations`}
        >
          <OrganisationsPerYearChart series={data.companyReportsOverTime} />
        </ChartCard>
        <ChartCard
          title="Company emissions over time"
          description="Overall group emissions from company reports"
        >
          <EmissionsOverTimeChart series={data.companyReportsOverTime} />
        </ChartCard>
      </DashboardGrid>
    </DashboardSection>
  );
}

function PurchasedGoodsSection({
  data,
  unlocked,
}: {
  data: GroupMainDashboard;
  unlocked: boolean;
}) {
  const description = 'Emissions from purchased food, drink and packaging';

  if (!unlocked) {
    return <LockedSection title="Purchased goods" description={description} />;
  }

  return (
    <DashboardSection
      title="Purchased goods"
      description={description}
      actions={
        <ExploreButton
          label="Explore purchasing emissions"
          to={GROUP_ROUTES.PURCHASING_EMISSIONS}
        />
      }
    >
      <DashboardGrid className="lg:grid-cols-2">
        <ChartCard
          title="Organisations reporting purchasing emissions"
          description={`Of ${data.totalOrganizations} member organisations`}
        >
          <OrganisationsPerYearChart series={data.purchasedGoodsOverTime} />
        </ChartCard>
        <ChartCard
          title="Purchasing emissions over time"
          description="Overall group emissions from purchased goods"
        >
          <EmissionsOverTimeChart series={data.purchasedGoodsOverTime} />
        </ChartCard>
      </DashboardGrid>
    </DashboardSection>
  );
}

function ProductsSection({
  data,
  unlocked,
}: {
  data: GroupMainDashboard;
  unlocked: boolean;
}) {
  const description = 'Product carbon assessments, rated on our A-E scale';

  const ratingCounts = useMemo(
    () =>
      Object.fromEntries(
        data.productsByRating.map((bucket) => [
          bucket.emissionsCategory,
          bucket.count,
        ])
      ) as Partial<Record<EmissionsCategoryEnum, number>>,
    [data.productsByRating]
  );

  // Single series with a per-row fill — recharts colours each bar from its
  // data entry, so every bar gets the rounded top from `topRadius`.
  const ratingChartData = useMemo(
    () =>
      RATING_ORDER.map((rating) => {
        const count = ratingCounts[rating] ?? 0;
        return {
          rating,
          items: count,
          fill: `var(--rating-${rating.toLowerCase()})`,
          percent: data.totalProductCount
            ? Math.round((count / data.totalProductCount) * 100)
            : 0,
        };
      }),
    [ratingCounts, data.totalProductCount]
  );

  if (!unlocked) {
    return <LockedSection title="Products" description={description} />;
  }

  const percentByRating = Object.fromEntries(
    ratingChartData.map((row) => [row.rating, row.percent])
  ) as Partial<Record<EmissionsCategoryEnum, number>>;

  return (
    <DashboardSection
      title="Products"
      description={description}
      actions={
        <ExploreButton
          label="Explore product assessments"
          to={GROUP_ROUTES.PRODUCTS}
        />
      }
    >
      <DashboardGrid>
        <div className="grid grid-cols-1 gap-4">
          <MetricCard
            title="Products assessed"
            value={formatCompact(data.totalProductCount)}
          />
          <MetricCard
            title="Collections assessed"
            value={formatCompact(data.totalCollectionCount)}
          />
          <MetricCard
            title="Average carbon intensity"
            value={
              data.averageCarbonIntensity != null ? (
                <span className="flex items-center gap-2">
                  <RatingCloud
                    rating={data.averageEmissionsCategory}
                    className="w-9"
                  />
                  <span>{data.averageCarbonIntensity.toFixed(2)}</span>
                </span>
              ) : (
                '-'
              )
            }
            unit={data.averageCarbonIntensity != null ? 'kgCO₂e/kg' : undefined}
            description="Emissions produced per kg of food produced"
          />
        </div>
        <ChartCard
          title="Products by carbon rating"
          description="Distribution of all assessed products on the A–E scale"
          className="lg:col-span-2"
        >
          <SimpleBarChart
            chartConfig={RATING_DISTRIBUTION_CHART_CONFIG}
            chartData={ratingChartData}
            className="aspect-auto size-full"
            margin={{ top: 20, right: 10, left: 10, bottom: 10 }}
            nameKey="rating"
            dataKey="items"
            topRadius={4}
            showBarLabels
            YAxisProps={{ hide: true }}
            XAxisProps={{
              interval: 0,
              height: 70,
              tick: <RatingAxisTick percentByRating={percentByRating} />,
            }}
          />
        </ChartCard>
      </DashboardGrid>
    </DashboardSection>
  );
}

function OrganisationsPerYearChart({
  series,
}: {
  series: readonly { year: number; organizationsCount: number }[];
}) {
  const chartData = useMemo(
    () => [...series].sort((a, b) => a.year - b.year),
    [series]
  );

  return (
    <SimpleBarChart
      chartConfig={ORGS_CHART_CONFIG}
      chartData={chartData}
      className="aspect-auto size-full"
      margin={{ top: 20, right: 10, left: 10, bottom: 10 }}
      nameKey="year"
      dataKey="organizationsCount"
      topRadius={4}
      showBarLabels
      YAxisProps={{ hide: true }}
    />
  );
}

function EmissionsOverTimeChart({
  series,
}: {
  series: readonly { year: number; totalEmissionsTonnes: number | null }[];
}) {
  const chartData = useMemo(
    () => [...series].sort((a, b) => a.year - b.year),
    [series]
  );
  // Label the last point that has a value — trailing in-progress years come
  // through with null emissions and would otherwise swallow the label.
  const lastIndex = chartData.findLastIndex(
    (point) => point.totalEmissionsTonnes != null
  );

  return (
    <SimpleAreaChart
      chartConfig={EMISSIONS_CHART_CONFIG}
      chartData={chartData}
      className="aspect-auto size-full"
      margin={{ top: 24, right: 20, left: 0, bottom: 10 }}
      nameKey="year"
      dataKey="totalEmissionsTonnes"
      gradientFill
      dot
      strokeWidth={2}
      YAxisProps={{
        tickFormatter: formatCompact,
        width: 64,
        label: {
          value: 'tCO₂e',
          angle: -90,
          position: 'insideLeft',
          className: 'fill-gray-500 text-xs',
          style: { textAnchor: 'middle' },
        },
      }}
      tooltipValueFormatter={(value) => formatTonnes(value, { compact: true })}
      areaLabel={({ x, y, value, index }) =>
        index === lastIndex && value != null ? (
          <text
            x={x}
            y={y - 12}
            textAnchor="end"
            className="fill-foreground"
            fontSize={11}
            fontWeight={600}
          >
            {formatTonnes(value, { compact: true })}
          </text>
        ) : null
      }
    />
  );
}
